import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { GenderButton } from '../../utilities/GenderButton.tsx';
import { ReptoolModal } from '../../utilities/ReptoolModal.tsx';
import { ChangeMorphModal } from './ChangeMorphModal.tsx';
import { GrowthChart } from '../../GrowthChart.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Carousel } from 'react-responsive-carousel';
import { CollectionCareRecord, CollectionCareRecordType, Pairing, TreeIndividual, WeightEntry } from '../types/CollectionTypes.ts';
import { BasicFetch } from '../../../utils/BasicFetch.ts';
import { FamilyTreeEditModal, OpenCreateModalInfo } from './FamilyTreeEditModal.tsx';
import { Pairings, CreatePairingModal } from '../../family_tree/Pairings.tsx';
import { QRCode } from "react-qrcode-logo";
import Lightbox from "yet-another-react-lightbox";
import { formatDate } from '../../utilities/FormatDate.ts';
import { EditPhotosModal } from './PictureModal.tsx';
import { Table, Button, FormCheck, Form, Alert, Tabs, Tab, Popover, Offcanvas, Badge } from 'react-bootstrap';
import { faCamera, faArrowRight, faArrowLeft, faArrowDown, faCog, faTimes, faArrowUp, faCircle, faSpinner, faInfoCircle, faEllipsisV, faBars, faTag, faTags, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import { ReptoolFamilyTree } from '../../family_tree/FamilyTree.tsx';
import { CollectionReptile } from '../types/CollectionTypes.ts';
import { geneExpressions } from '../../../types/Gene.ts';
import { driver, DriveStep } from 'driver.js';
import { Loading } from '../../Loading.tsx';
import { FamilyIndividualEditModal } from './FamilyIndividualEditModal.tsx';
import { InquireToBuyModal } from './InquireToBuyModal.tsx';
import { ReptileCareInfoModal } from './ReptileCareInfoModal.tsx';
import { Gene } from '../../../types/Gene.ts';
import { Morph } from '../../../types/Morph.ts';
import { Reptile } from '../../../types/Reptile.ts';
import { SelectCollectionReptileModal } from "../../clutches/modals/SelectCollectionReptileModal.tsx"
import { UserInfo } from '../../../types/UserInfo.ts';
import { SubscriptionModal } from '../../stripe/SubscriptionModal.tsx';
import { CollectionReptileLabelModal } from './CollectionReptileLabelModal.tsx'
import { ListingPanel } from '../../store/ListingPanel.tsx';
import { useRepToolContext } from '../../../RepToolContext.tsx';
type FamilyView = "Lineage" | "Offspring" | "Pairings";
export type CollectionReptileModalSettings = {
  sexFilter: "male" | "female";

}


const getLastDate = (records: CollectionCareRecord[]) => {
  const record = records.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
  return record ? formatDate(record.date).toLocaleDateString() : "mm/dd/yyyy";
}

const setControls = ({
  appendControls,
  activeIndex,
  setActiveIndex,
  hiddenPanels,
  setOpenInquireModal,
  toggleShowMenu,
  showInquiry,
  openPictureModal,
  readOnly
}: {
  activeIndex: number,
  appendControls: (controls: JSX.Element | null, name: string) => void,
  setActiveIndex: (index: number) => void,
  hiddenPanels: number[],
  setOpenInquireModal: () => void,
  toggleShowMenu: () => void,
  showInquiry: boolean,
  openPictureModal: () => void,
  readOnly
}) => {
  appendControls(
    <CarouselControls
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      hiddenPanels={hiddenPanels}
      setOpenInquireModal={setOpenInquireModal}
      toggleShowMenu={toggleShowMenu}
      showInquiry={showInquiry}
      openPictureModal={openPictureModal}
      readOnly={readOnly}
    />, 'Collection Reptile Modal'
  )
}


const activeSteps = (activeIndex: number, readOnly: boolean) => {
  let allSteps = [] as DriveStep[];
  let editOnlySteps = [] as DriveStep[];


  if (activeIndex === 0) {
    allSteps = [];
    editOnlySteps = [
      { element: "#listingPrice", popover: { title: 'Listing Price', description: 'This is the price you are asking for this reptile. You can change this at any time.' } },
      { element: "#firmOnPrice", popover: { title: 'Firm on Price', description: 'If this is checked, you are not willing to negotiate on the price.' } },
      { element: "#listingDescription", popover: { title: 'Description', description: 'This is a description of the reptile you are listing. You can change this at any time.' } },
    ];
  } else if (activeIndex === 1) {
    allSteps = [
      { element: '#icon-qr-container', popover: { title: 'QR Code', description: 'This QR code can be scanned to view this reptile\'s information on it\'s own webpage or to be used by another RepTool app and add it to another user\'s collection.' } },
    ]
    editOnlySteps = [
      {
        element: '#edit-reptile', popover: {
          title: 'Edit Genetics',
          description: 'Click here to change the genetics for this reptile.',
        }
      }
    ]

  } else if (activeIndex === 2) {
    allSteps = [
      {
        element: '#morph-records', popover: {
          title: 'Records',
          description: 'Here you can see the most recent records for this reptile. Click on any of the dates to edit them.',
        },
      },
      {
        element: '#collection-dialog #gender-btn',
        popover: {
          title: 'Sex Button',
          description: 'This is this animal\'s sex. Tap on it to cycle through female, male or unsexed.'
        }
      },
      {
        element: '#collectionName',
        popover: {
          title: 'Name',
          description: 'This is the name of this reptile. Tap on it to change it.'
        }
      }
    ]

    editOnlySteps = [
      {
        element: '#camera', popover: {
          title: 'Edit Photo',
          description: 'Tap here to replace the photo for this reptile. Only 1 photo is supported at this time.',
        }
      },
      {
        element: '#save-btn',
        popover: {
          title: 'Save',
          description: 'Tap here to save any changes you\'ve made.',
        }
      }
    ]
  } else if (activeIndex === 3) {
    allSteps = [
      {
        element: '#growth-chart-container', popover: {
          title: 'Growth Table',
          description: 'This table is to record the weight of this reptile over time. Click on any of the dates or weights to edit them. This is a great way to track the growth of your reptile or to keep an eye on your animals health.',
        }
      }
    ]

    editOnlySteps = [
      {
        element: '#add-weight-btn', popover: {
          title: 'Add New Entry',
          description: 'Click here to add a new entry to the growth table.',
        }
      }
    ]
  } else if (activeIndex === 4) {
    // growth chart
    allSteps = [
      {
        element: '#growth-chart', popover: {
          title: 'Growth Chart',
          description: 'This is a visual representation of the growth table. This is a great way to track the growth of your reptile or to keep an eye on your animals health.',
        }
      }
    ]
  } else if (activeIndex === 5) {
    // family tree
    allSteps = [
      {
        element: '#lineageTree', popover: {
          title: 'Family Tree',
          description: 'This is a visual representation of the family tree for this reptile. Click on any of the reptiles to view their details.',
        }
      },
    ]
    editOnlySteps = [
      {
        element: '#lineageTree', popover: {
          title: 'Creating a family member',
          description: 'Click on one of the \'+\' tiles to be prompted to create a new family member. You can use a reptile in your current collection or create one as a placeholder.',
        }
      },
      {
        element: '#lineageTree', popover: {
          title: 'Edit Family Tree',
          description: 'Tap on one of the parents of this animal and update the name, breeder or remove the animal from the family tree.',
        }
      }
    ]
  }


  return readOnly ? allSteps : allSteps.concat(editOnlySteps);

}



const QrPanel = ({
  reptile,
  setOpenChangeModal,
  readOnly,
  morphs,
  isSpecies
}: {
  reptile: CollectionReptile,
  setOpenChangeModal: (open: boolean) => void,
  readOnly: boolean,
  morphs: Morph[],
  isSpecies: boolean
}) => {
  const genes = reptile.genes?.length ? reptile.genes : morphs?.find(morph => morph.morph_name_id === reptile.morph_name_id)?.genes || [];
  const qrCodeUrl = `${(window as any).serverLocation}collection_reptile/get/${reptile.uuid}/user/${reptile.user_id}`;
  return (
    <div className={`qr-code-container no-pad `} data-num='0'>
      <div id="icon-qr-container">
        {reptile.id >= 1 ? <QRCode
          value={`${(window as any).serverLocation}collection_reptile/get/${reptile.uuid}/user/${reptile.user_id}`}
          fgColor={'#000'}
          bgColor={'#fff'}
          size={128}
          logoWidth={70}
          logoHeight={70}
        /> : <p>A QR code will be generated after saving for the first time.</p>}
      </div>
      {(window as any).serverLocation === "http://127.0.0.1:5000/" && <div className='text-center'>
        <Button href={qrCodeUrl} target='_blank'>View</Button>
      </div>}
      {!readOnly && <div className="mt-3">
        <div id='edit-reptile' className='button' onClick={() => setOpenChangeModal(true)}>
          Change {isSpecies ? 'Species' : 'Genetics'}</div>
      </div>}
      <div className='mt-3'>
        <Table size='sm' variant='dark'>
          <tbody>
            <tr>
              <th>
                {isSpecies ? 'Species' : 'Morph'}
              </th>
              <td className='text-left pl-3'>{reptile.morph_name}</td>
            </tr>
            {!isSpecies && genes && <tr className='border-top'>
              <th>Genes</th>
              <td className='text-left pl-3'>
                {genes.map((gene) => {
                  const expression = geneExpressions.find(x => x.value === gene.expression);
                  return <div>
                    {gene.gene_name}
                    {expression?.value !== "NORMAL" && <small> ({expression?.label})</small>}
                  </div>
                })}
              </td>
            </tr>}
          </tbody>
        </Table>
      </div>
    </div>
  )
}

const Header = ({
  reptile,
  setReptile,
  readOnly
}: {
  reptile: CollectionReptile | null,
  setReptile: Function,
  readOnly: boolean
}) => {
  const [currentName, setCurrentName] = useState<string>(reptile?.name === reptile?.morph_name ? "" : reptile?.name || "");

  useEffect(() => {
    setCurrentName(reptile?.name === reptile?.morph_name ? "" : reptile?.name || "");
  }, [reptile])


  const changeGender = () => {
    if (readOnly || !reptile) {
      return;
    }

    let newGender = "none";
    if (reptile.gender === "male") {
      newGender = "female";
    } else if (reptile.gender === "female") {
      newGender = "none";
    } else if (reptile.gender === "none") {
      newGender = "male";
    }
    const data = new FormData();
    data.append("sex", newGender);
    BasicFetch({
      url: `${(window as any).serverLocation}collection_reptile/${reptile.id}/edit_sex`,
      data,
      method: 'POST',
      contentType: 'multipart/form-data',
      onSuccess: (data: { reptile: CollectionReptile }) => {
        setReptile({ ...data.reptile });
      },
      onFail: () => {
        (window as any).alert("There was an error changing sex, please try again later");
      },
    })

  }

  const updateName = () => {
    if (readOnly || !reptile) {
      return;
    }
    const data = new FormData();
    data.append("name", currentName);

    BasicFetch({
      url: `${(window as any).serverLocation}collection_reptile/${reptile.id}/edit_name`,
      data,
      contentType: 'multipart/form-data',
      method: 'POST',
      onSuccess: (data: { reptile: CollectionReptile }) => {
        setReptile({ ...data.reptile });
      },
      onFail: () => {
        (window as any).alert("There was an error changing name, please try again later");
      },
    })
  }



  return (
    <div className='d-flex flex-column'>
      <div className="d-flex flex-row container px-0">
        {reptile && <GenderButton onChange={changeGender} gender={reptile.gender} />}
        <h3 className="modal-title-container flex-fill">
          {!readOnly ? <input
            id="collectionName"
            readOnly={readOnly}
            value={currentName}
            placeholder='Name'
            onBlur={updateName}
            onChange={(event) => {
              setCurrentName(event.target.value);
              setReptile({ ...reptile, name: event.target.value })
            }}
            className="modal-title border text-center"
          /> :
            currentName}
        </h3>
      </div>
      {false && <div className='text-center'>
        <FontAwesomeIcon icon={faChevronDown} size='xl' className='text-light' />
      </div>}
    </div>
  )
}
const InfoPanel = ({
  reptile,
  setReptile,
  readOnly,
  setOpenRecordsModal,
}: {
  reptile: CollectionReptile,
  setReptile: Function,
  readOnly: boolean,
  setOpenRecordsModal: (record_key: CollectionCareRecordType) => void

}) => {
  const [displayPhoto, setDisplayPhoto] = useState(reptile.picture);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [openLightbox, setOpenLightbox] = useState(false);

  useEffect(() => {
    setDisplayPhoto(reptile.picture)
  }, [reptile.picture])

  const pictureSlides = reptile.pictures.map((picture) => {
    return {
      src: picture.url,
    }
  });


  const saveChanges = (reptile: CollectionReptile) => {
    const infoDates = ["last_fed", "last_def", "last_substrate", "last_shed", "hatch_date"]

    let reptileData = new FormData();

    infoDates.forEach(d => {
      const value = reptile[d] && reptile[d] !== "" ? formatDate(reptile[d]).toUTCString() : null;
      reptileData.append(d, value)
    })


    BasicFetch({
      url: `${(window as any).serverLocation}collection_reptile/${reptile.id}/edit_dates`,
      data: reptileData,
      method: 'POST',
      contentType: 'multipart/form-data',
      onSuccess: (data: { reptile: CollectionReptile }) => {
        setReptile({ ...data.reptile });
      },
      onFail: () => {
        (window as any).alert("There was an error saving changes, please try again later");
      },
    })
  }

  const setUpCalendar = (key: string, title: string, value: Date) => {
    if (readOnly) return;
    (window as any).setUpCalendar(
      title,
      () => (date: Date) => {
        saveChanges({ ...reptile, [key]: date });
        (window as any).closeCalendar();
      },
      value
    );
  }


  return (
    <div id="rep-info" className={`no-pad`} data-num='1'>
      <Lightbox
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        slides={pictureSlides}
      />

      <div className='d-flex flex-column'>
        {!loadingPhoto ? <div
          className="keeper-dialog-img d-flex flex-column flex-fill no-pad"
          onClick={() => setOpenLightbox(true)}
          style={{ backgroundImage: `url(${displayPhoto ? displayPhoto : "images/notavailable.jpg"})` }}
        >
        </div> : <div className="d-flex flex-column flex-fill no-pad">
          <FontAwesomeIcon icon={faSpinner} spin size='3x' />
        </div>}
        <div className={`mb-${reptile.labels.length ? 0 : 3} mt-3 d-flex flex-row justify-content-between align-items-center`}>
          {false && <h6 id="photo-credit" className="col bg-black">{''}</h6>}
          <div className='d-flex flex-row flex-wrap my-auto'>
            {reptile.labels.map(label => (
              <Badge bg='dark' className='h-100 mr-2 mb-2'>{label.text}</Badge>
            ))}
          </div>
        </div>

        <div id='morph-records' className="row text-center no-pad">
          <div className="col-4 collection-field weight no-pad columns">
            <h6>Weight</h6>
            <h5 id="weight" className='d-flex flex-column my-3'>
              {reptile.weight.length && reptile.weight[reptile.weight.length - 1].weight ? <span>{reptile.weight[reptile.weight.length - 1].weight}</span> : <span className='text-muted'>0</span>}
            </h5>
          </div>
          <div className="col-4 collection-field fed no-pad columns">
            <h6>Last Fed</h6>
            <h5
              id="fed-date" className='d-flex flex-column my-3'
              onClick={() => {
                if (readOnly) {
                  return;
                }
                setOpenRecordsModal("feed_dates");
              }}>
              {!!reptile.feed_dates.length ? <span>{getLastDate(reptile.feed_dates)}</span> : <span className='text-muted'>mm/dd/yyyy</span>}
            </h5>
          </div>
          <div className="col-4 collection-field substrate no-pad columns">
            <h6>Substrate Change</h6>
            <h5 id="substrate-date" className='d-flex flex-column my-3'
              onClick={() => {
                if (readOnly) {
                  return;
                }
                setOpenRecordsModal("substrate_dates");
              }}>
              {reptile.substrate_dates.length ? <span>{getLastDate(reptile.substrate_dates)}</span> : <span className='text-muted'>mm/dd/yyyy</span>}
            </h5>
          </div>
          <div className="col-4 collection-field shed no-pad columns">
            <h6>Last Shed</h6>
            <h5 id="shed-date" className='d-flex flex-column my-3'
              onClick={() => {
                if (readOnly) {
                  return;
                }
                setOpenRecordsModal("shed_dates")
              }}>
              {reptile.shed_dates.length ? <span>{getLastDate(reptile.shed_dates)}</span> : <span className='text-muted'>mm/dd/yyyy</span>}
            </h5>
          </div>
          <div className="col-4 collection-field defecation no-pad columns">
            <h6>Last Defecation</h6>
            <h5 id="last-def" className='d-flex flex-column my-3' onClick={() => {
              if (readOnly) {
                return;
              }
              setOpenRecordsModal("def_dates")
            }}>
              {reptile.def_dates.length ? <span>{getLastDate(reptile.def_dates)}</span> : <span className='text-muted'>mm/dd/yyyy</span>}
            </h5>
          </div>
          <div className="col-4 collection-field hatch no-pad columns">
            <h6>Hatch Date</h6>
            <h5 id="hatch-date" className='d-flex flex-column my-3' onClick={() => {
              if (readOnly) {
                return;
              }
              setUpCalendar('hatch_date', 'Hatch Date', new Date(reptile.hatch_date) || new Date());
            }}>
              {reptile.hatch_date ? <span>{formatDate(reptile.hatch_date).toLocaleDateString()}</span> : <span className='text-muted'>mm/dd/yyyy</span>}
            </h5>
          </div>
        </div>

      </div>
    </div >
  )
}

const GrowthWeightDataColumns = ({
  reptile,
  rowData,
  setReptile,
  canRemove,
  readOnly
}: {
  reptile: CollectionReptile,
  rowData: WeightEntry,
  setReptile: (reptile: CollectionReptile) => void,
  canRemove: boolean,
  readOnly: boolean
}) => {
  const [currentWeight, setCurrentWeight] = useState(rowData.weight);


  const removeWeight = (id) => {
    BasicFetch({
      url: `${(window as any).serverLocation}collection_reptile/${reptile.id}/weight/${id}/delete`,
      method: 'DELETE',
      onSuccess: (data: { reptile: CollectionReptile }) => {
        setReptile({ ...data.reptile });
      },
      onFail: () => {
        (window as any).alert("There was an error saving changes, please try again later");
      },
    })
  }
  const dateChanged = (date) => {
    const data = new FormData();
    data.append("date", formatDate(date).toUTCString());
    data.append("weight", currentWeight.toString());

    BasicFetch({
      url: `${(window as any).serverLocation}collection_reptile/${reptile.id}/weight/${rowData.id}/edit`,
      data,
      method: 'POST',
      contentType: 'multipart/form-data',
      onSuccess: (data: { reptile: CollectionReptile }) => {
        setReptile({ ...data.reptile });
      },
      onFail: () => {
        (window as any).alert("There was an error saving changes, please try again later");
      },
    })

  }

  const weightChanged = () => {

    const data = new FormData();
    data.append("weight", currentWeight.toString());
    data.append("date", formatDate(rowData.date).toUTCString());

    BasicFetch({
      url: `${(window as any).serverLocation}collection_reptile/${reptile.id}/weight/${rowData.id}/edit`,
      data,
      method: 'POST',
      contentType: 'multipart/form-data',
      onSuccess: (data: { reptile: CollectionReptile }) => {
        setReptile({ ...data.reptile });
      },
      onFail: () => {
        (window as any).alert("There was an error saving changes, please try again later");
      },
    })
  }
  const setUpCalendar = (value: Date) => {
    if (readOnly) return;
    (window as any).setUpCalendar(
      "",
      () => (date: Date) => {
        dateChanged(date);
        (window as any).closeCalendar();
      },
      value
    );
  }

  return (
    <>
      <td>{canRemove && !readOnly && <Button variant='outline-light' onClick={() => removeWeight(rowData.id)}><FontAwesomeIcon icon={faTimes} /></Button>}</td>
      <td onClick={() => {
        if (readOnly) {
          return;
        }
        setUpCalendar(rowData.date ? new Date(rowData.date) : new Date());
      }} >
        {rowData.date ? <span>{formatDate(rowData.date).toLocaleDateString()}</span> : <span className='text-muted'>mm/dd/yyyy</span>}
      </td>
      <td className='text-center'>
        {!readOnly ? <input type='number' readOnly={readOnly} className='form-control form-control-sm my-auto' onBlur={weightChanged} onChange={(event) => setCurrentWeight(parseFloat(event.target.value))} value={currentWeight} /> : currentWeight}
      </td>
    </>
  )
}

const GrowthTablePanel = ({
  reptile,
  setReptile,
  readOnly
}: {
  reptile: CollectionReptile
  setReptile: (reptile: CollectionReptile) => void
  readOnly: boolean
}) => {

  const rows = reptile.weight.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const addNewRow = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}collection_reptile/${reptile.id}/weight/create`,
      extra: {
        return_record: true
      },
      method: 'POST',
      onSuccess: (data: { weight: WeightEntry }) => {
        setReptile({ ...reptile, weight: [...reptile.weight, data.weight] });
      },
      onFail: () => {
        (window as any).alert("There was an error saving changes, please try again later");
      },
    })

  }

  return (
    <React.Fragment>

      <div id="growth-chart-container" className={`no-pad`} data-num='2'>
        <Table size='sm' responsive striped variant='dark' >
          <thead className='border-0'>
            <tr>
              <th><div className='button d-none'><FontAwesomeIcon icon={faCog} /></div></th>
              <th>Date</th>
              <th>Weight</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, x) => {
              const diff = x > 0 ? rows[x].weight - rows[x - 1].weight : 0;
              // check how many decimals
              const weightString = row.weight.toString();
              const decimalIndex = weightString.indexOf('.');
              const decimalLength = decimalIndex > -1 ? Math.min(weightString.length - decimalIndex - 1, 2) : 0;
              return (
                <tr key={x}>
                  <GrowthWeightDataColumns
                    reptile={reptile}
                    rowData={row}
                    setReptile={setReptile}
                    canRemove={rows.length > 1 && !readOnly}
                    readOnly={readOnly}
                  />
                  <td>
                    {diff !== 0 && rows[x].weight !== 0 && <div className='d-flex flex-column px-2'>
                      <small className={diff > 0 ? 'text-success' : 'text-danger'}>
                        <FontAwesomeIcon icon={diff > 0 ? faArrowUp : faArrowDown} />
                      </small>
                      <small>{diff.toFixed(decimalLength)}</small>
                    </div>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </div>
      {!readOnly && <div className=" mt-3 text-center">
        <Button id='add-weight-btn' variant='outline-light' onClick={addNewRow} className="add-row w-100 py-2">Add New Entry</Button>
      </div>}
    </React.Fragment >

  )
}

const GrowthChartPanel = ({ reptile }: { reptile: CollectionReptile }) => {
  const filteredWeight = reptile.weight.filter(row => row.weight !== 0 && formatDate(row.date) !== "").sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  const dataPoints = filteredWeight.map((row) => { return { weight: row.weight, date: formatDate(row.date).getTime() } })
  let dateMin = 0;
  let dateMax = 0;
  if (filteredWeight.length > 0) {
    dateMin = formatDate(filteredWeight[0].date);
    dateMax = formatDate(filteredWeight[filteredWeight.length - 1].date);
  }

  return (
    <div>
      <div id="growth-chart" className=" no-pad">
        {filteredWeight.length > 1 ? <GrowthChart
          dataPoints={dataPoints}
          name={reptile.name || reptile.morph_name}
          startDate={dateMin}
          endDate={dateMax} /> :
          <div className='text-center'>
            <p>Not enough weight data currently available.</p>
            <p>Fill out at least 2 weight entries on the previous page to get started.</p>
          </div>}

      </div>
    </div>
  )
}
const FamilyTreePanel = ({
  reptile,
  setReptile,
  setCreatingFamilyInfo,
  setEditingFamilyIndividual,
  readOnly,
  openPairingModal,
  showingPairingModal,
  userInfo,
}: {
  reptile: CollectionReptile,
  setReptile: (reptile: CollectionReptile) => void,
  setEditingFamilyIndividual: (individual: TreeIndividual) => void,
  setCreatingFamilyInfo: (info: OpenCreateModalInfo) => void,
  readOnly: boolean,
  openPairingModal?: (pairing: Pairing) => void,
  showingPairingModal: boolean,
  userInfo?: UserInfo,
}) => {


  const [activeView, setActiveView] = useState<FamilyView>('Lineage');
  const numberOfActiveTabs = readOnly ? 1 : 2;
  return (
    <div id="lineageTree" className={`no-pad justify-content-center`} data-num='4'>
      {numberOfActiveTabs > 1 && <Tabs defaultActiveKey={activeView} id="family-tree-tabs" className='mb-3' onSelect={(key) => setActiveView(key as FamilyView)}>
        <Tab eventKey='Lineage' title='Lineage'>
          <div className='lineage-tree'>
            <ReptoolFamilyTree
              reptile={reptile}
              readOnly={readOnly}
              setEditingFamilyIndividual={setEditingFamilyIndividual}
              setCreatingFamilyInfo={setCreatingFamilyInfo}
              setReptile={setReptile}
            />
          </div>
        </Tab>
        {!readOnly && <Tab eventKey='Pairings' title='Pairings'>
          <Pairings
            readOnly={readOnly}
            userInfo={userInfo}
            currentReptile={reptile}
            openPairingModal={openPairingModal}
            refetchPairings={showingPairingModal}
          />
        </Tab>}
      </Tabs>}
      {numberOfActiveTabs === 1 && <div className='lineage-tree'>
        <ReptoolFamilyTree
          reptile={reptile}
          readOnly={readOnly}
          setEditingFamilyIndividual={setEditingFamilyIndividual}
          setCreatingFamilyInfo={setCreatingFamilyInfo}
          setReptile={setReptile}
        />
      </div>}
    </div>
  )
}


const CarouselControls = ({
  activeIndex,
  setActiveIndex,
  hiddenPanels,
  setOpenInquireModal,
  toggleShowMenu,
  showInquiry,
  openPictureModal,
  readOnly
}: {
  activeIndex: number,
  setActiveIndex: (index: number) => void,
  hiddenPanels: number[],
  setOpenInquireModal: () => void,
  toggleShowMenu: () => void,
  showInquiry: boolean,
  openPictureModal: () => void,
  readOnly
}) => {
  const panelIndexes = [0, 1, 2, 3, 4, 5];
  const visiblePanels = panelIndexes.filter(index => !hiddenPanels.includes(index));
  const nextPanel = () => {
    let nextIndex = activeIndex + 1;
    while (!visiblePanels.includes(nextIndex)) {
      if (nextIndex > visiblePanels[visiblePanels.length - 1]) {
        nextIndex = visiblePanels[0];
      } else {
        nextIndex++;
      }
    }
    setActiveIndex(nextIndex);
  }
  const previousPanel = () => {
    let nextIndex = activeIndex - 1;
    while (!visiblePanels.includes(nextIndex)) {
      if (nextIndex < visiblePanels[0]) {
        nextIndex = visiblePanels[visiblePanels.length - 1];
      } else {
        nextIndex--;
      }
    }
    setActiveIndex(nextIndex);
  }
  return (
    <div className="text-center w-100 hideElementOnKeyboardShown">

      <div className='text-center d-flex justify-content-center w-100'>
        {showInquiry && <div>
          <Button variant='outline-light' onClick={setOpenInquireModal} className='mx-auto'>Inquire</Button>
        </div>}
        <div className='ml-auto d-flex flex-row'>
          {!readOnly && <FontAwesomeIcon id="camera-btn" size='xl' icon={faCamera} className='ml-3 my-auto' onClick={openPictureModal} />}
          <FontAwesomeIcon icon={faBars} size='xl' className='ml-3 my-auto' onClick={toggleShowMenu} />
        </div>
      </div>
      {false && <div className="d-flex flex-row w-100 justify-content-around">
        <div id="left-button" className="px-3 my-auto" onClick={previousPanel}>
          <FontAwesomeIcon size='xl' icon={faArrowLeft} />
        </div>
        <div id='carousel-indicators' className='d-flex flex-row justify-content-center'>
          {panelIndexes.map(index => {
            if (hiddenPanels.includes(index)) {
              return null;
            }
            return (
              <div key={index} className={`${activeIndex === index ? 'active circle' : 'circle'}`} onClick={() => setActiveIndex(index)}>
                <FontAwesomeIcon size='sm' icon={faCircle} />
              </div>
            )
          })}
        </div>
        <div id="right-button" className="px-3 my-auto" onClick={nextPanel} >
          <FontAwesomeIcon size='xl' icon={faArrowRight} />
        </div>
      </div>}

    </div>
  )
}

export const CollectionReptileModal = ({
  reptileId,
  propReptile,
  closeModal,
  refreshReptile,
  readOnly,
  showInquiry = false,
  genes,
  morphs,
  isSpecies = false,
  userInfo,
  activePanel = 2
}: {
  reptileId?: string,
  propReptile?: CollectionReptile,
  closeModal: (() => void) | null,
  refreshReptile?: (reptile: CollectionReptile) => void,
  readOnly: boolean,
  showInquiry?: boolean,
  morphs: Morph[],
  genes: Gene[],
  isSpecies: boolean,
  userInfo?: UserInfo,
  activePanel?: number
}) => {
  const [reptile, setReptile] = useState<CollectionReptile | null>(propReptile || null);
  const [showMenu, setShowMenu] = useState(false);
  const [activeIndex, setActiveIndex] = useState(activePanel);
  const [openChangeModal, setOpenChangeModal] = useState(false);
  const [openPictureModal, setOpenPictureModal] = useState(false);
  const [editingFamilyIndividual, setEditingFamilyIndividual] = useState<TreeIndividual | null>(null);
  const [openEditFamilyModal, setOpenEditFamilyModal] = useState(false);
  const [openInquireModal, setOpenInquireModal] = useState(false);
  const [editingPairing, setOpenPairingModal] = useState<Pairing | null>(null);
  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false);
  const [openLabelModal, setOpenLabelModal] = useState(false);
  const { appendControls } = useRepToolContext();

  const [collectionReptileSettings, setCollectionReptileSettings] = useState<CollectionReptileModalSettings | null>(null);
  // This is used to toggle an update in the pairing section. This will then get set back to null once the update is complete
  // TODO: figure out how to handle this better
  const [selectedCollectionReptile, setSelectedCollectionReptile] = useState<CollectionReptile | null>(null);

  // this is used to determine if the user is editing records
  // this opens the records modal
  const [recordsModalKey, setRecordsModalKey] = useState<CollectionCareRecordType | null>(null);
  // This is used to determine if the user is editing the family tree
  // the family id is the id of the family in which this member is a parent. The sex is to determine if
  // the parent is a mother or father
  const [creatingFamilyInfo, setCreatingFamilyInfo] = useState<OpenCreateModalInfo | null>(null);
  const allSteps = activeSteps(activeIndex, readOnly);

  const driverObj = driver({
    steps: allSteps
  })

  if (reptile?.name === "") {
    reptile.name = reptile.morph_name;
  }

  useEffect(() => {
    if (reptileId === undefined) {
      return;
    }
    BasicFetch({
      url: `${(window as any).serverLocation}collection_reptile/${reptileId}/get_uuid`,
      method: 'GET',
      onSuccess: (data: { reptile: CollectionReptile }) => {
        setReptile({ ...data.reptile });
      },
      onFail: () => {
        (window as any).alert("There was an error loading this reptile, please try again later");
      },
    })
  }, [reptileId]);

  useEffect(() => {
    if (propReptile) {
      setReptile(propReptile);
    }
  }, [propReptile]);

  useEffect(() => {
    setActiveIndex(activePanel);
  }, [activePanel]);

  const showGrowthTable = !!reptile && (reptile.weight.length > 0 || !readOnly);
  const showGrowthChart = !!reptile && (reptile.weight.length > 1 || !readOnly);
  const showFamilyTree = !!reptile && ((reptile.treeData.father_family || reptile.treeData.mother_family) || !readOnly);
  const showListingPanel = !!reptile;

  useEffect(() => {
    if (!creatingFamilyInfo && !openChangeModal && !openPictureModal && !editingFamilyIndividual && !openInquireModal && !recordsModalKey && !openSubscriptionModal) {
      let hiddenPanels: number[] = [];
      if (!showListingPanel) {
        hiddenPanels.push(0);
      }

      if (!showGrowthTable) {
        hiddenPanels.push(3);
      }
      if (!showGrowthChart) {
        hiddenPanels.push(4);
      }
      if (!showFamilyTree) {
        hiddenPanels.push(5);
      }

      setControls({
        appendControls,
        activeIndex,
        setActiveIndex,
        hiddenPanels,
        setOpenInquireModal: () => setOpenInquireModal(true),
        toggleShowMenu: () => setShowMenu(!showMenu),
        showInquiry,
        readOnly,
        openPictureModal: () => setOpenPictureModal(true)
      });
    }
  }, [creatingFamilyInfo, openChangeModal, openPictureModal, reptile, activeIndex, readOnly, editingFamilyIndividual, openInquireModal, recordsModalKey, editingPairing, openSubscriptionModal, showMenu]);

  useEffect(() => {
    if (reptile && refreshReptile) {
      refreshReptile(reptile);
    }
  }, [reptile]);

  const changeSlideAndClose = (index: number) => {
    setShowMenu(false);
    setActiveIndex(index);
  }

  const actionText = !readOnly ? "View / Edit" : "View";

  return (
    <>
      <Offcanvas className='bg-black border-right px-4 text-light' show={showMenu} onHide={() => setShowMenu(false)}>
        <Offcanvas.Header className='text-light d-flex justify-content-between pl-0' >
          <h3 className='mb-0'>{readOnly ? 'View' : 'Manage'} Reptile</h3>
          <FontAwesomeIcon onClick={() => setShowMenu(false)} icon={faTimes} size='2x' style={{ cursor: 'pointer' }} />
        </Offcanvas.Header>
        <div className='d-flex flex-column'>
          {!readOnly && <>
            <h5 className='mt-4'>Reptile Labels</h5>
            <Button className='mb-3' variant='outline-light' onClick={() => { setOpenLabelModal(true); setShowMenu(false) }}>
              {actionText} Labels
            </Button>
          </>}
          <h5 className='mt-4'>Care</h5>
          <Button className='mb-3' variant='outline-light' onClick={() => changeSlideAndClose(2)}>
            {actionText} Care Records
          </Button>
          {showGrowthChart && showGrowthTable && <>
            <h5 className='mt-4'>Growth</h5>
            <Button className='mb-3' variant='outline-light' onClick={() => changeSlideAndClose(3)}>
              {actionText} Growth Data
            </Button>
            <Button className='mb-3' variant='outline-light' onClick={() => changeSlideAndClose(4)}>
              {actionText} Growth Chart
            </Button>
          </>}
          <h5 className='mt-4'>Breeding</h5>
          <Button className='mb-3' variant='outline-light' onClick={() => changeSlideAndClose(1)}>
            {actionText} Genetics & QR Code
          </Button>
          {showFamilyTree && <Button className='mb-3' variant='outline-light' onClick={() => changeSlideAndClose(5)}>
            {actionText} Lineage {!readOnly && '& Pairings'}
          </Button>}
          <h5 className='mt-4'>
            {readOnly ? 'Listing' : 'Sell'}
          </h5>
          <Button className='mb-3' variant='outline-light' onClick={() => changeSlideAndClose(0)}>
            {actionText} Listing
          </Button>
        </div>
      </Offcanvas>
      {reptile && <>
        {openPictureModal &&
          <EditPhotosModal
            closeModal={() => setOpenPictureModal(false)}
            reptile={reptile}
            setReptile={setReptile}
            readonly={readOnly}
          />}
        <CollectionReptileLabelModal show={openLabelModal} collectionReptile={reptile} closePopup={() => setOpenLabelModal(false)} setReptile={setReptile} />
        {openSubscriptionModal && userInfo && <SubscriptionModal userInfo={userInfo} closeModal={() => setOpenSubscriptionModal(false)} />}
        {openInquireModal &&
          <InquireToBuyModal
            closeModal={() => setOpenInquireModal(false)}
            reptile={reptile}
          />
        }
        {
          editingPairing && userInfo &&
          <CreatePairingModal
            closeModal={() => setOpenPairingModal(null)}
            collectionReptile={reptile}
            selectedCollectionReptileMatch={selectedCollectionReptile}
            setSelectedCollectionReptileMatch={setSelectedCollectionReptile}
            setCollectionReptileSettings={setCollectionReptileSettings}
            editingPairing={editingPairing}
            openSubscriptionModal={() => setOpenSubscriptionModal(true)}
            userInfo={userInfo}
          />
        }
        {
          collectionReptileSettings &&
          <SelectCollectionReptileModal
            closeModal={() => setCollectionReptileSettings(null)}
            onSelect={(reptile) => {
              setSelectedCollectionReptile(reptile);
              setCollectionReptileSettings(null);
            }}
            sexFilter={collectionReptileSettings.sexFilter}
          />
        }
        {openChangeModal &&
          <ChangeMorphModal
            setReptile={setReptile}
            collectionReptile={reptile}
            closeModal={() => setOpenChangeModal(false)}
            genes={genes}
            morphs={morphs}
            isSpecies={isSpecies}
          />}
        {creatingFamilyInfo &&
          <FamilyTreeEditModal
            collectionReptile={reptile}
            closeModal={() => setCreatingFamilyInfo(null)}
            setReptile={setReptile}
            morphs={morphs}
            genes={genes}
            creatingFamilyInfo={creatingFamilyInfo}
          />}

        {editingFamilyIndividual && openEditFamilyModal &&
          <FamilyIndividualEditModal
            closeModal={() => { setOpenEditFamilyModal(false); setEditingFamilyIndividual(null) }}
            editingFamilyIndividual={editingFamilyIndividual}
            reptile={reptile}
            setReptile={setReptile}
            genes={genes}
            morphs={morphs}
          />
        }
        {recordsModalKey &&
          <ReptileCareInfoModal
            closeModal={() => setRecordsModalKey(null)}
            collectionReptile={reptile}
            setReptile={setReptile}
            careType={recordsModalKey}
            readOnly={readOnly}
          />
        }
      </>}

      <ReptoolModal fullSize modalBodyClass='container' id="collection-dialog" closeModal={closeModal} showTutorial={driverObj.drive} title={
        <Header
          reptile={reptile}
          setReptile={setReptile}
          readOnly={readOnly}

        />} body={(

          !reptile ? <Loading /> : <Carousel
            className='collection-slider'
            swipeable={false}
            showIndicators={false}
            selectedItem={activeIndex}
            showStatus={false}
            showArrows={false}
            showThumbs={false}
          >
            {showListingPanel ? <ListingPanel
              reptile={reptile}
              setReptile={setReptile}
              readOnly={readOnly}
            /> : <></>}
            <QrPanel
              reptile={reptile}
              setOpenChangeModal={setOpenChangeModal}
              readOnly={readOnly}
              morphs={morphs}
              isSpecies={isSpecies}
            />
            <InfoPanel
              reptile={reptile}
              readOnly={readOnly}
              setReptile={setReptile}
              setOpenRecordsModal={setRecordsModalKey}
            />
            {showGrowthTable ?
              <GrowthTablePanel
                reptile={reptile}
                setReptile={setReptile}
                readOnly={readOnly}
              />
              : <></>}
            {showGrowthChart ? <GrowthChartPanel reptile={reptile} /> : <></>}

            {showFamilyTree ? <FamilyTreePanel
              userInfo={userInfo}
              reptile={reptile}
              readOnly={readOnly}
              setReptile={setReptile}
              setEditingFamilyIndividual={(indiv) => { setEditingFamilyIndividual(indiv); setOpenEditFamilyModal(true) }}
              setCreatingFamilyInfo={setCreatingFamilyInfo}
              openPairingModal={(pairing) => setOpenPairingModal(pairing)}
              setCollectionReptileSettings={setCollectionReptileSettings}
              selectedCollectionReptile={selectedCollectionReptile}
              setSelectedCollectionReptile={setSelectedCollectionReptile}
              showingPairingModal={editingPairing ? true : false}
            /> : <></>}
          </Carousel>
        )} />
    </>
  )
}

(window as any).createCollectionView = ({ reptileId, userId }) => {
  ReactDOM.render(<LoadingOrModal reptileId={reptileId} userId={userId} />, document.getElementById('app'));
}
export const LoadingOrModal = ({ reptileId, userId }: {
  reptileId: string,
  userId: number
}) => {
  const [fetchingData, setFetchingData] = useState(true);
  const [reptileData, setReptileData] = useState<CollectionReptile | null>(null);
  const [genes, setGenes] = useState<Gene[]>([]);
  const [morphs, setMorphs] = useState<Morph[]>([]);
  const [selectedReptile, setSelectedReptile] = useState<Reptile | null>(null);
  const getData = () => {

    BasicFetch({
      url: `${(window as any).serverLocation}collection_reptile/get/${reptileId}/user/${userId}`,
      method: 'GET',
      extra: { data_only: true },
      onSuccess: (data) => {
        setReptileData(data);
        setSelectedReptile(data.reptile)
        setFetchingData(false);
      },
      onFail: () => alert("There was an issue fetching this reptile's data. Please try again later."),
      onFinish: () => setFetchingData(false)
    });
  }
  const getMorphData = () => {
    BasicFetch({
      url: `${(window as any).serverLocation}/morph/reptile/${selectedReptile}/get`,
      method: 'GET',
      onSuccess: (data) => {
        setGenes(data.genes);
        setMorphs(data.morphs);
      },
      onFail: () => alert("There was an issue fetching morph data. Please try again later.")
    })
  }


  (window as any).getPhotos = () => { };

  useEffect(() => {
    getData();
  }, [])


  useEffect(() => {
    if (selectedReptile) {
      getMorphData();
    }
  }, [selectedReptile])

  return (
    fetchingData || !reptileData ? null :
      <CollectionReptileModal
        reptileId={reptileId}
        closeModal={null}
        readOnly={true}
        showInquiry={true}
        morphs={morphs}
        genes={genes}
        isSpecies={selectedReptile?.controls?.isSpecies || false}
      />

  )
}